import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO, SITE_URL } from "@/lib/useSEO";
import { useAuth } from "@/context/AuthContext";
import { Gauge, Fuel, Users, Cog, MapPin, ArrowLeft, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ phone: "", message: "", start_date: "", end_date: "" });
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: car ? `${car.title} — ${car.year}` : "Αυτοκίνητο",
    description: car ? `${car.title} ${car.year} · ${car.body_type} · ${car.fuel} · ${car.horsepower} ίπποι · ${car.mileage_km?.toLocaleString()} χλμ — ${car.listing_type === "rent" ? "Ενοικίαση" : "Πώληση"}. ${car.description?.slice(0, 100) || ""}` : undefined,
    path: `/cars/${id}`,
    image: car?.images?.[0],
    jsonLd: car ? {
      "@context": "https://schema.org",
      "@type": "Car",
      "name": car.title,
      "brand": { "@type": "Brand", "name": car.make },
      "model": car.model,
      "vehicleModelDate": String(car.year),
      "bodyType": car.body_type,
      "fuelType": car.fuel,
      "vehicleTransmission": car.transmission,
      "numberOfDoors": car.specs?.Doors,
      "mileageFromOdometer": { "@type": "QuantitativeValue", "value": car.mileage_km, "unitCode": "KMT" },
      "image": car.images,
      "description": car.description,
      "offers": {
        "@type": "Offer",
        "price": car.price,
        "priceCurrency": "EUR",
        "availability": car.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": `${SITE_URL}/cars/${id}`,
      }
    } : null,
  });

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (!car?.images?.length) return;
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowRight") setActiveImage((i) => (i + 1) % car.images.length);
      if (e.key === "ArrowLeft") setActiveImage((i) => (i - 1 + car.images.length) % car.images.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxOpen, car]);

  const handleSubmit = async () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/cars/${id}`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { car_id: id, ...form });
      toast.success("Το αίτημα υποβλήθηκε. Θα επικοινωνήσουμε σύντομα.");
      setShowModal(false);
      setForm({ phone: "", message: "", start_date: "", end_date: "" });
    } catch (e) {
      toast.error("Η υποβολή απέτυχε. Δοκιμάστε ξανά.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!car) return <div className="pt-40 text-center text-gray-500 tracking-widest uppercase text-xs font-display">Φόρτωση...</div>;

  const isRent = car.listing_type === "rent";
  const price = isRent ? `${car.price.toLocaleString()} €/ημέρα` : `${car.price.toLocaleString()} €`;

  return (
    <div data-testid="car-detail-page" className="pt-20">
      {/* Cinematic header image */}
      <div className="relative h-[80vh] min-h-[600px] overflow-hidden">
        <img
          src={car.images?.[activeImage] || car.images?.[0]}
          alt={car.title}
          onClick={() => car.images?.length > 0 && setLightboxOpen(true)}
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 cursor-zoom-in"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent pointer-events-none" />
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="absolute top-8 left-6 md:left-12 flex items-center gap-2 text-sm tracking-widest uppercase text-white/70 hover:text-white z-10 font-display"
        >
          <ArrowLeft size={14} /> Πίσω
        </button>
        <div className="absolute bottom-12 left-0 right-0 max-w-7xl mx-auto px-6 md:px-12 pointer-events-none">
          <div className="label-caps mb-3">{car.year} · {car.body_type} · {isRent ? "Ενοικίαση" : "Πώληση"}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-wide leading-tight">
            {car.title}
          </h1>
        </div>
      </div>

      {/* Thumbnail strip */}
      {car.images?.length > 1 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex gap-3 overflow-x-auto">
          {car.images.map((img, idx) => (
            <button
              key={idx}
              data-testid={`thumb-${idx}`}
              onClick={() => setActiveImage(idx)}
              className={`relative shrink-0 w-32 h-20 overflow-hidden border-2 transition-all ${
                activeImage === idx ? "border-[var(--gold)]" : "border-white/10 opacity-60 hover:opacity-100"
              }`}
            >
              <img src={img} alt={`${car.title} ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-12">
          {/* Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pb-12 border-b border-white/5">
            {[
              { icon: Gauge, label: "Ισχύς", value: `${car.horsepower} ίπποι` },
              { icon: Cog, label: "Κιβώτιο", value: car.transmission === "Automatic" ? "Αυτόματο" : "Χειροκίνητο" },
              { icon: Fuel, label: "Καύσιμο", value: { Petrol: "Βενζίνη", Diesel: "Πετρέλαιο", Electric: "Ηλεκτρικό", Hybrid: "Υβριδικό" }[car.fuel] || car.fuel },
              { icon: Users, label: "Θέσεις", value: car.seats },
            ].map((s, i) => (
              <div key={i}>
                <s.icon size={18} className="text-[var(--gold)] mb-3" />
                <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest mb-1 font-display">{s.label}</div>
                <div className="text-lg font-display font-semibold">{s.value}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="label-caps mb-4">Επισκόπηση</div>
            <p className="text-lg text-gray-300 leading-relaxed font-light">{car.description}</p>
          </div>

          {car.features?.length > 0 && (
            <div>
              <div className="label-caps mb-6">Χαρακτηριστικά & Εξοπλισμός</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {car.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-base text-gray-300 py-2 border-b border-white/5">
                    <Check size={14} className="text-[var(--gold)]" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="label-caps mb-4">Λεπτομέρειες</div>
            <div className="grid grid-cols-2 gap-y-3 text-base">
              <div className="text-gray-500">Χιλιόμετρα</div>
              <div className="text-gray-200">{car.mileage_km.toLocaleString()} χλμ</div>
              <div className="text-gray-500">Τοποθεσία</div>
              <div className="text-gray-200 flex items-center gap-1"><MapPin size={12} /> {car.location}</div>
              <div className="text-gray-500">Μάρκα</div>
              <div className="text-gray-200">{car.make}</div>
              <div className="text-gray-500">Μοντέλο</div>
              <div className="text-gray-200">{car.model}</div>
            </div>
          </div>

          {car.specs && Object.keys(car.specs).length > 0 && (
            <div>
              <div className="label-caps mb-6">Πλήρη Χαρακτηριστικά</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-3 text-base">
                {Object.entries(car.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/5 py-2">
                    <span className="text-gray-500">{k}</span>
                    <span className="text-gray-200 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky inquiry card */}
        <div className="lg:sticky lg:top-32 self-start">
          <div className="bg-[#121212] border border-[var(--gold)]/20 p-8">
            <div className="text-[0.65rem] text-gray-500 uppercase tracking-widest mb-2 font-display">
              {isRent ? "Τιμή Ενοικίασης" : "Τιμή Πώλησης"}
            </div>
            <div className="font-display text-4xl font-extrabold gold-gradient mb-2">{price}</div>
            <div className="text-sm text-gray-500 mb-8 italic">{isRent ? "Ελάχιστη ενοικίαση 1 ημέρα" : "Συζητήσιμη"}</div>

            <button
              data-testid="inquire-btn"
              onClick={() => setShowModal(true)}
              className="btn-primary w-full"
            >
              {isRent ? "Κράτηση Οχήματος" : "Αίτημα Αγοράς"}
            </button>
            <div className="mt-4 text-center text-xs text-gray-500 tracking-widest uppercase font-display">
              Ή τηλεφωνήστε +30 693 322 3378
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowModal(false)}>
          <div className="bg-[#121212] border border-[var(--gold)]/30 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="label-caps mb-3">{isRent ? "Αίτημα Κράτησης" : "Αίτημα Αγοράς"}</div>
            <h3 className="font-display text-2xl font-bold mb-6">{car.title}</h3>

            {!user ? (
              <>
                <p className="text-base text-gray-400 mb-6 font-light">
                  Παρακαλούμε συνδεθείτε για να υποβάλετε το αίτημά σας.
                </p>
                <button onClick={handleSubmit} data-testid="modal-signin" className="btn-primary w-full">
                  Σύνδεση με Google
                </button>
              </>
            ) : (
              <div className="space-y-4">
                {isRent && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[0.65rem] uppercase tracking-widest text-gray-500 font-display">Ημ/νία Έναρξης</label>
                      <input type="date" data-testid="modal-start" className="input-line" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-[0.65rem] uppercase tracking-widest text-gray-500 font-display">Ημ/νία Λήξης</label>
                      <input type="date" data-testid="modal-end" className="input-line" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-[0.65rem] uppercase tracking-widest text-gray-500 font-display">Τηλέφωνο</label>
                  <input type="tel" data-testid="modal-phone" placeholder="+30 69X XXX XXXX" className="input-line" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="text-[0.65rem] uppercase tracking-widest text-gray-500 font-display">Μήνυμα</label>
                  <textarea data-testid="modal-message" rows={3} placeholder="Πες μας περισσότερα..." className="input-line resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <div className="flex gap-3 pt-4">
                  <button data-testid="modal-cancel" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Ακύρωση</button>
                  <button data-testid="modal-submit" onClick={handleSubmit} disabled={submitting} className="btn-primary flex-1">
                    {submitting ? "Υποβολή..." : "Υποβολή"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && car.images?.length > 0 && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-6 right-6 h-12 w-12 flex items-center justify-center text-white/70 hover:text-white border border-white/10 hover:border-[var(--gold)] transition-colors z-10"
          >
            <X size={20} />
          </button>

          {car.images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImage((i) => (i - 1 + car.images.length) % car.images.length);
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 h-14 w-14 flex items-center justify-center text-white/70 hover:text-[var(--gold)] border border-white/10 hover:border-[var(--gold)] transition-colors z-10"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImage((i) => (i + 1) % car.images.length);
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 h-14 w-14 flex items-center justify-center text-white/70 hover:text-[var(--gold)] border border-white/10 hover:border-[var(--gold)] transition-colors z-10"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <img
            src={car.images[activeImage]}
            alt={car.title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[90vh] object-contain"
          />

          <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2 pointer-events-none">
            <div className="text-[0.65rem] tracking-[0.3em] uppercase text-white/60 font-display">
              {car.title}
            </div>
            {car.images.length > 1 && (
              <div className="text-xs tracking-widest text-[var(--gold)]">
                {activeImage + 1} / {car.images.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
